import TextInput from '../TextInput';
import { useEffect, useState } from 'react';
import TextArea from '../TextArea.jsx';
import ConditionalBuilder from './ConditionalBuilder.jsx';
import TaggableInput from '../TaggableInput.jsx';
import { Actions } from './Actions.jsx';
import { EnvelopeIcon } from '@heroicons/react/24/outline/index.js';
import DurationInput from '../DurationInput.jsx';
import { TriggerTypesToHuman } from '../../lib/Flows.js';
import Select from '../Select';
import Tooltip from '~/components/Tooltip';
import InputLabel from '../InputLabel';
import { useFeatures, FEATURE_INVOICES } from '~/lib/Features';

export const DEFAULT_ACTION = 'email';

export const MARKETING_AUTOMATION_APPLIES_TO_OPTIONS = {
    'App\\Models\\Client': 'Clients',
    'App\\Models\\BookingCalendarEvent': 'Events',
    'App\\Models\\ClassInstance': 'Classes',
};

const ActionButton = ({ isSelected, title, icon, onClick }) => {
    return (
        <button
            className={`${isSelected ? 'text-primary-700 bg-primary-50 border-primary-700' : 'text-black border-white'} bg-white border py-2 px-4 rounded-md grid items-center justify-items-center sm:gap-2 sm:grid-cols-[max-content,max-content] sm:justify-items-start sm:justify-content-start`}
            onClick={onClick}
            type="button"
        >
            <div className="text-primary-700 w-4 h-4">{icon}</div>
            <div>{title}</div>
        </button>
    );
};

const StepEdit = ({
    data,
    setData,
    doSubmit,
    isSubmitting,
    schema,
    triggerTypes,
    onDelete,
    isDeleting,
    onSave,
    onCancel,
    isSaving,
    middleContent,
    isMarketingAutomation = false,
}) => {
    const { isEnabled } = useFeatures();
    function submit(ev) {
        ev.preventDefault();
        if (onSave) {
            onSave(data);
        } else {
            doSubmit();
        }
    }

    function setSettings(newSettings) {
        setData(old => {
            if (typeof newSettings === 'function') {
                newSettings = newSettings(old?.settings);
            }

            return { ...old, settings: newSettings };
        });
    }

    useEffect(() => {
        if (triggerTypes?.length === 1) {
            // If there's only 1 option then choose it!
            setData(old => ({ ...old, trigger: triggerTypes[0] }));
        }
    }, [triggerTypes?.length]);

    const SettingsComponent = Actions[data.action].settings;
    const triggerOptions = { '': 'Choose...' };
    triggerTypes.forEach(
        trigger => (triggerOptions[trigger] = TriggerTypesToHuman[trigger]),
    );

    return (
        <form onSubmit={submit} className="">
            <div
                className={
                    !isMarketingAutomation &&
                    'max-h-[70vh] scrollbar-visible pb-12 overflow-y-auto [scrollbar-gutter:stable]'
                }
            >
                {/*{isMarketingAutomation && (*/}
                {/*    <div className="mb-4">*/}
                {/*        <InputLabel*/}
                {/*            showTooltip={true}*/}
                {/*            message="This tells Breely what the automation should watch for and respond to."*/}
                {/*        >*/}
                {/*            Apply To*/}
                {/*        </InputLabel>*/}
                {/*        <Select*/}
                {/*            //label="Apply To"*/}
                {/*            value={data.flowable_type || 'App\\Models\\Client'}*/}
                {/*            onChange={value =>*/}
                {/*                setData(old => ({*/}
                {/*                    ...old,*/}
                {/*                    flowable_type: value,*/}
                {/*                }))*/}
                {/*            }*/}
                {/*            options={applyToOptions}*/}
                {/*        />*/}
                {/*    </div>*/}
                {/*)}*/}

                <div className="input-label">Action</div>
                <div className="grid gap-2 grid-cols-2 md:grid-cols-3">
                    {Object.keys(Actions)
                        .filter(action => {
                            // Hide SMS actions for marketing automations
                            if (
                                typeof Actions[action].hide === 'function' &&
                                Actions[action].hide({
                                    isMarketingAutomation,
                                    data,
                                    isEnabled,
                                })
                            ) {
                                return false;
                            }

                            return true;
                        })
                        .map(action => (
                            <ActionButton
                                key={action}
                                isSelected={data.action === action}
                                onClick={() =>
                                    setData({ ...data, action: action })
                                }
                                title={Actions[action].name}
                                icon={Actions[action].icon}
                            />
                        ))}
                </div>

                <div className="input-label mt-4">Timing</div>
                <div className="flex gap-2 flex-col sm:flex-row sm:gap-4">
                    <div className="max-w-[126px] sm:w-full">
                        <DurationInput
                            value={data.offset}
                            onChange={value =>
                                setData({ ...data, offset: value })
                            }
                            id="offset"
                        />
                    </div>
                    <Select
                        className={`sm:w-full ${Object.keys(triggerOptions).length === 2 ? 'hidden' : ''}`}
                        id="trigger"
                        value={data.trigger}
                        onChange={value =>
                            setData(old => ({ ...old, trigger: value }))
                        }
                        options={triggerOptions}
                    />
                    {Object.keys(triggerOptions).length === 2 && (
                        <div className="mt-2">
                            {triggerOptions[data.trigger]}
                        </div>
                    )}
                </div>

                {!isMarketingAutomation && (
                    <div>
                        <div className="input-label mt-4">
                            Only do this when
                        </div>
                        <ConditionalBuilder
                            conditional={data?.condition}
                            setConditional={conditional =>
                                setData(old => ({
                                    ...old,
                                    condition: conditional,
                                }))
                            }
                            schema={schema}
                        />
                    </div>
                )}

                {isMarketingAutomation && (
                    <div>
                        <div className="mt-4">
                            <InputLabel
                                showTooltip={true}
                                message="Define the conditions that must be met for this automation to trigger."
                            >
                                Only Do This When
                            </InputLabel>
                            <ConditionalBuilder
                                conditional={
                                    data.target_conditions || {
                                        andor: 'and',
                                        conditions: [],
                                    }
                                }
                                setConditional={target_conditions =>
                                    setData(old => ({
                                        ...old,
                                        target_conditions,
                                    }))
                                }
                                schema={schema}
                            />
                        </div>

                        {middleContent}
                    </div>
                )}
                <InputLabel>
                    {Actions[data.action]?.name || 'Action'} Settings
                </InputLabel>
                <div className="p-4 rounded-lg bg-gray-200">
                    <SettingsComponent
                        settings={data.settings}
                        setSettings={setSettings}
                        schema={schema}
                        isMarketingAutomation={isMarketingAutomation}
                    />
                </div>
            </div>
            <div
                className={`${isMarketingAutomation ? 'sticky' : 'absolute px-10'} left-0 bottom-0 w-full flex flex-wrap gap-2 items-center justify-between bottom-0 py-6 bg-gray-100 bg-opacity-50 rounded-b-[23px] xs:flex-nowrap`}
            >
                <div className="flex flex-wrap gap-2 w-full">
                    <button type="submit" className="btn-save w-full xs:w-auto">
                        {isSubmitting || isSaving ? 'Saving...' : 'Save'}
                    </button>
                    {onCancel && (
                        <button
                            type="button"
                            className="btn-light-pill w-full xs:w-auto"
                            onClick={onCancel}
                        >
                            Cancel
                        </button>
                    )}
                </div>
                {typeof onDelete === 'function' && (
                    <button
                        type="button"
                        className="btn-pink-pill-lg w-full xs:w-auto"
                        onClick={onDelete}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                )}
            </div>
        </form>
    );
};

export default StepEdit;
